import React from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import { makeStyles, List, ListItem, ListItemAvatar, ListItemText, Typography, Tooltip, useTheme } from "@material-ui/core"
import moment from "moment"
import { ReadTheDocsSVG, DocumentSearchField, HeadLightBulb, TaskListSquarePerson } from "../../../components/icons"
import { getSourceSvg } from "../../../utils"

const useStyles = makeStyles((theme) => ({
	root: {
		width: "100%",
		paddingTop: 0,
		"& .MuiButtonBase-root.MuiListItem-root": {
			alignItems: "start",
		},
	},
	listItem: {
		paddingTop: 0,
		paddingBottom: 0,
		"&:hover": {
			cursor: "default",
		},
	},
	avatar: {
		minWidth: 40,
		paddingTop: 8,
	},
	text: {
		"& .MuiListItemText-primary": {
			fontSize: "inherit",
		},
		"& .MuiListItemText-secondary": {
			fontSize: 12,
			color: theme.palette.grey.D100,
		},
	},
	mainListDiv: {
		"&:hover": { backgroundColor: theme.palette.action.hover },
	},
}))

const RecentActivityList = ({ rows, handleClick }) => {
	const classes = useStyles()
	const [t] = useTranslation("global")
	const theme = useTheme()
	const history = useHistory()

	const docName = (row) =>
		row?.entity_metadata?.entity_name || row?.history?.current_object?.name || row?.history?.current_object?.description

	const docDisplayName = (row) =>
		row?.entity_type === "search"
			? `${row?.history?.current_object?.name}`
			: row?.entity_type === "concept"
			? `${row?.history?.current_object?.description}`
			: row?.entity_type === "review"
			? row?.history?.current_object?.name
			: row?.entity_metadata?.entity_name
			? `${row?.entity_metadata?.entity_name}`
			: "-"

	const docDescriptionText = (row) =>
		row?.entity_type === "search"
			? `${t("dashboard.youHaveCreated")} ${row?.history?.current_object?.name} ${t("search.search")}`
			: row?.entity_type === "concept"
			? `${t("dashboard.youHaveCreated")} ${row?.history?.current_object?.description} ${t("dashboard.concept")}`
			: row?.entity_type === "review"
			? `${t("dashboard.youHaveCreated")} ${row?.history?.current_object?.name} ${t("review.review")}`
			: row?.entity_metadata?.entity_name
			? `${t("dashboard.youHaveOpened")} ${row?.entity_metadata?.entity_name} ${t("dashboard.document")}`
			: "-"

	const ShowTitle = ({ row }) => {
		return (
			<Tooltip arrow title={docDescriptionText(row)} placement="top">
				<Typography
					style={{
						...theme.typography.fieldValue,
						textOverflow: "ellipsis",
						overflow: "hidden",
						display: "-webkit-box",
						WebkitLineClamp: 2,
						WebkitBoxOrient: "vertical",
						color: theme.palette.text.primary,
						wordBreak: "break-all",
					}}
				>
					{row?.entity_type === "search" ? (
						<>
							<span style={{ marginRight: 3, color: theme.palette.text.primary }}>{t("dashboard.youHaveCreated")}</span>
							<span style={{ marginRight: 3, fontWeight: 500, color: theme.palette.text.primary }}>
								{row?.history?.current_object?.name}
							</span>
							<span style={{ color: theme.palette.text.primary }}>{t("search.search")}</span>
						</>
					) : row?.entity_type === "concept" ? (
						<>
							<span style={{ marginRight: 3, color: theme.palette.text.primary }}>{t("dashboard.youHaveCreated")}</span>
							<span style={{ marginRight: 3, fontWeight: 500, color: theme.palette.text.primary }}>
								{row?.history?.current_object?.description}
							</span>
							<span style={{ color: theme.palette.text.primary }}>{t("dashboard.concept")}</span>
						</>
					) : row?.entity_type === "review" ? (
						<>
							<span style={{ marginRight: 3, color: theme.palette.text.primary }}>{t("dashboard.youHaveCreated")}</span>
							<span style={{ marginRight: 3, fontWeight: 500, color: theme.palette.text.primary }}>
								{row?.history?.current_object?.name}
							</span>
							<span style={{ color: theme.palette.text.primary }}>{t("review.review")}</span>
						</>
					) : row?.entity_type === "file" ? (
						<>
							<span style={{ marginRight: 3, color: theme.palette.text.primary }}>{t("dashboard.youHaveOpened")}</span>
							<span style={{ marginRight: 3, fontWeight: 500, color: theme.palette.text.primary }}>
								{row?.entity_metadata?.entity_name}
							</span>
							<span style={{ color: theme.palette.text.primary }}>{t("dashboard.document")}</span>
						</>
					) : (
						"-"
					)}
				</Typography>
			</Tooltip>
		)
	}

	const fileClickHandler = (row) =>
		row?.entity_type === "search"
			? history.push({
					pathname: `view-documents`,
					state: {
						searchObject: row?.history?.current_object,
					},
			  })
			: row?.entity_type === "concept"
			? history.push({
					pathname: `build-concept`,
					state: {
						conceptObject: row?.history?.current_object,
					},
			  })
			: row?.entity_type === "file"
			? history.push({
					pathname: `${row?.context?.page_context}/viewer/${row?.entity_id}?${row?.entity_metadata?.payload}`,
			  })
			: row?.entity_type === "review"
			? history.push({
					pathname: `view-documents`,
					state: {
						reviewId: row?.entity_id,
					},
			  })
			: null

	return (
		<List className={classes.root}>
			{rows.map((row) => (
				<div style={{ display: "flex", flexDirection: "column", paddingBottom: 8 }} className={classes.mainListDiv}>
					<ListItem
						className={classes.listItem}
						button
						onClick={(e) => {
							handleClick(e, row)
						}}
						disableRipple
					>
						<ListItemAvatar className={classes.avatar}>
							<div>
								<div
									style={{
										background: "rgba(241, 130, 38, 0.2)",
										height: 30,
										width: 30,
										borderRadius: "50%",
										position: "absolute",
										zIndex: 1,
									}}
								></div>
								<div
									style={{
										paddingLeft: 8,
										paddingTop: 1,
									}}
								>
									{row?.entity_type === "file" ? (
										<ReadTheDocsSVG style={{ zIndex: 2 }} />
									) : row?.entity_type === "concept" ? (
										<HeadLightBulb style={{ zIndex: 2 }} />
									) : row?.entity_type === "review" ? (
										<TaskListSquarePerson style={{ zIndex: 2 }} />
									) : (
										<DocumentSearchField style={{ zIndex: 2 }} />
									)}
								</div>
							</div>
						</ListItemAvatar>
						<ListItemText
							className={classes.text}
							primary={<ShowTitle row={row} />}
							secondary={`${row?.timestamp ? moment.utc(row?.timestamp).local().format("LT") : "-"} | ${
								row?.timestamp ? moment.utc(row?.timestamp).local().format("L") : "-"
							}`}
						/>
					</ListItem>
					<div style={{ paddingLeft: 55, width: "80%", height: 41 }}>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "start",
								border: `1px dashed ${theme.typography.button.disabledColor}`,
								height: 41,
							}}
						>
							{row?.entity_type !== "search" && row?.entity_type !== "concept" && row?.entity_type !== "review" && (
								<ListItemAvatar style={{ paddingLeft: 10, minWidth: 23, paddingRight: 11 }}>
									{getSourceSvg(row?.entity_metadata?.entity_subtype)}
								</ListItemAvatar>
							)}
							<ListItemText
								className={classes.text}
								style={{
									...(row?.entity_type === "search" || row?.entity_type === "concept" || row?.entity_type === "review"
										? { paddingLeft: 10 }
										: {}),
								}}
								primary={
									docName(row) ? (
										<Tooltip arrow title={docName(row)} placement="top">
											<Typography
												style={{
													width: "95%",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
													overflow: "hidden",
													cursor: "pointer",
													...theme.typography.body2,
													color: theme.palette.grey.D100,
													fontWeight: theme.typography.fontWeightMedium,
												}}
											>
												{docDisplayName(row)}
											</Typography>
										</Tooltip>
									) : (
										"-"
									)
								}
								onClick={() => fileClickHandler(row)}
							/>
						</div>
					</div>
				</div>
			))}
		</List>
	)
}

export default RecentActivityList
